/**
 * The enhanced odds diffusion adapter handles
 * connection & relaying messages from Diffusion.
 * Handles connection, subscription, unsubscription.
 *
 */
import Router from '../../controllers/Router';
import MessageBusTopics from '../../../common/MessageBusTopics';
import { subscribe, unsubscribe, unsubscribeAll } from '@push-component/client';
import { GTP_ENHANCED_ODDS } from '@push-component/constants';

const OWNER = 'sportsbook-web-enhanced-odds';

/**
 * Global PubSub reference
 *
 * @type {Object}
 *
 * @private
 */
const pubSub = window.WH.messageBus;

/**
 * Subscribe to various messages.
 *
 * @returns {void}
 *
 * @private
 */
const bindPubSubMessages = () => {
    pubSub.subscribe(MessageBusTopics.ENHANCED_ODDS_SUBSCRIBE, handlePubSubSubscription);
    pubSub.subscribe(MessageBusTopics.ENHANCED_ODDS_UNSUBSCRIBE, handlePubSubUnsubscription);
};

/**
 * Handles pubSub subscription
 *
 * @param {String}   topic  The pubsub topic
 * @param {Object} payload  Subscription payload
 *
 * @returns {void}
 *
 * @private
 */
const handlePubSubSubscription = (topic, { eventId }) => {
    api.subscribe(eventId, (data) => {
        pubSub.publish(MessageBusTopics.ENHANCED_ODDS_MESSAGE, { GTP_ENHANCED_ODDS, eventId, data });
    });
};

/**
 *
 * Handles pubSub unsubscription
 *
 * @param {String}   topic  The pubsub topic
 * @param {Object} payload  Subscription payload
 *
 * @returns {void}
 *
 * @private
 */
const handlePubSubUnsubscription = (topic, { eventId }) => {
    api.unsubscribe(eventId);
};

/**
 * Initialise module.
 *
 * @return {void}
 *
 * @private
 */
const init = () => {
    bindPubSubMessages();
    Router.hooks.add(api.unsubscribeAll);  // reset on url change;
};

/**
 * Public API for this component
 *
 * @type {Object}
 *
 * @public
 */
const api = {

    /**
   * Subscribe action - Subscribes to Push topic based on
   * data received (eventId).
   *
   * @param {String}    eventId  event id.
   * @param {Function} callback  subscriber callback to call with was price data.
   *
   * @return {Boolean} true, if subscription was done and successful
   *
   * @public
   */
    subscribe: (eventId, callback) => {
        if (typeof callback !== 'function') {
            return false;
        }

        subscribe({
            type: GTP_ENHANCED_ODDS,
            owner: OWNER,
            eventId,
            callback: ({ data }) => callback(data),
        });

        return true;
    },

    /**
   * Unsubscribe from enhanced odds for given eventId (and optionally for given callback).
   * Note if no callback is given, then _all_ subscriptions are removed for this event.
   *
   * @param {String}    eventId  event id to unsubscribe from enhancedOdds for.
   *
   * @return {void}
   *
   * @public
   */
    unsubscribe: (eventId) => {
        unsubscribe({
            type: GTP_ENHANCED_ODDS,
            owner: OWNER,
            eventId,
        });

    },

    /**
   * Unsubscribe from all topics.
   *
   * @return {void}
   *
   * @public
   */
    unsubscribeAll: () => {
        unsubscribeAll({
            type: GTP_ENHANCED_ODDS,
            owner: OWNER,
        });

    },
};

init();

export default api;
